<template>
    <ValidationObserver ref="form" v-slot="{ invalid, validate, pristine }">
        <form
            autocomplete="off"
            @keydown.enter.prevent=""
            @submit.prevent="validate().then(createUser)"
        >
            <div class="row">
                <div class="col-md-12">
                    <label>Role</label>
                    <ValidationProvider
                        v-slot="{ errors }"
                        name="Role"
                        rules="required"
                    >
                        <v-select
                            v-model="user.role"
                            :options="userRoles"
                            placeholder="Select role"
                            label="label"
                        >
                            <template slot="option" slot-scope="option">
                                {{ option.label }}
                            </template>
                        </v-select>
                        <label class="error">{{ errors[0] }}</label>
                    </ValidationProvider>
                </div>
            </div>
            <div class="row">
                <div v-if="showSelectCustomer" class="col-md-12">
                    <label>Customer</label>
                    <div class="form-group">
                        <v-select
                            v-model="selectedCustomer"
                            placeholder="Select customer"
                            label="name"
                            :options="customers"
                            @search="searchCustomers(...arguments)"
                        >
                            <template slot="no-options">
                                <span v-if="searchCustomersText">
                                    No results found.
                                </span>
                                <span v-else>Type to search...</span>
                            </template>
                            <template slot="option" slot-scope="option">
                                {{ option.name }}
                            </template>
                        </v-select>
                    </div>
                </div>
                <div v-if="showSelectDesigner" class="col-md-12">
                    <label>Designer</label>
                    <div class="form-group">
                        <v-select
                            v-model="selectedDesigner"
                            label="fullName"
                            placeholder="Select designer"
                            :options="designers"
                            @search="searchDesigners(...arguments)"
                        >
                            <template slot="no-options">
                                <span v-if="searchDesignersText">
                                    No results found.
                                </span>
                                <span v-else>Type to search...</span>
                            </template>
                            <template
                                slot="selected-option"
                                slot-scope="option"
                            >
                                {{ option.fullName }} -
                                {{ option.customer.name }}
                            </template>
                            <template slot="option" slot-scope="option">
                                <div>
                                    <b>{{ option.fullName }}</b>
                                </div>
                                <div>
                                    {{
                                        option.customer
                                            ? option.customer.name
                                            : ""
                                    }}
                                </div>
                            </template>
                        </v-select>
                    </div>
                </div>
            </div>
            <div v-show="user.role && selectedRelatedOptions">
                <div class="row">
                    <div class="col-md-6">
                        <ValidationProvider
                            v-slot="{ errors }"
                            name="First Name"
                            rules="required"
                        >
                            <base-input
                                id="fname"
                                v-model.trim="user.first_name"
                                name="fname"
                                autocomplete="off"
                                type="text"
                                label="First Name"
                                placeholder="First Name"
                                :error="errors[0]"
                            />
                        </ValidationProvider>
                    </div>
                    <div class="col-md-6">
                        <ValidationProvider
                            v-slot="{ errors }"
                            name="Last Name"
                            rules="required"
                        >
                            <base-input
                                id="lname"
                                v-model.trim="user.last_name"
                                name="lname"
                                autocomplete="off"
                                type="text"
                                label="Last Name"
                                placeholder="Last Name"
                                :error="errors[0]"
                            />
                        </ValidationProvider>
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-12">
                        <ValidationProvider
                            v-slot="{ errors }"
                            name="Username"
                            rules="required"
                        >
                            <base-input
                                id="username"
                                v-model.trim="userNameLower"
                                name="username"
                                type="text"
                                label="Username"
                                placeholder="Username"
                                autocomplete="new-username"
                                :error="errors[0]"
                            />
                        </ValidationProvider>
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-12">
                        <ValidationProvider
                            v-slot="{ errors }"
                            name="Email"
                            rules="required|email"
                        >
                            <base-input
                                id="email"
                                v-model.trim="user.email"
                                name="email"
                                type="email"
                                label="Email address"
                                placeholder="Email"
                                :error="errors[0]"
                            />
                        </ValidationProvider>
                    </div>
                    <div class="col-md-12">
                        <p class="small">
                            * A welcome email with the username and a randomly
                            generated password will be sent to the above email.
                        </p>
                    </div>
                </div>
            </div>
            <base-button
                id="createUser"
                :disabled="invalid || !selectedRelatedOptions || pristine"
                native-type="submit"
                type="primary"
                class="btn-fill"
            >
                Add User
            </base-button>
        </form>
    </ValidationObserver>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import vSelect from "vue-select";

export default {
    name: "UserAddForm",
    components: {
        ValidationObserver,
        ValidationProvider,
        vSelect,
    },
    props: {
        customers: {
            type: Array,
            required: true,
        },
        designers: {
            type: Array,
            required: true,
        },
        reset: {
            type: Boolean,
            default: false,
        },
        userRoles: {
            type: Array,
            required: true,
        },
        userAdded: {
            type: Boolean,
            required: true,
        },
        userDetails: {
            type: Object,
            default: null,
        },
        availableUsername: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            searchDesignersText: null,
            searchCustomersText: null,
            showSelectCustomer: false,
            showSelectDesigner: false,
            user: {
                first_name: null,
                last_name: null,
                role: null,
                username: null,
                email: null,
                customer_id: null,
                designer_id: null,
            },
        };
    },
    computed: {
        selectedRelatedOptions() {
            if (this.user.role === "ClientBasic") {
                return !!this.selectedDesigner;
            }
            if (this.user.role === "ClientAdmin") {
                return !!this.selectedCustomer;
            }
            return true;
        },
        selectedDesigner: {
            get() {
                return {
                    id: this.user.designer_id,
                    fullName: this.user.designer_name,
                    customer: {
                        id: this.user.customer_id,
                        name: this.user.customer_name,
                    },
                };
            },
            set(value) {
                this.user.customer_id = value?.customer.id;
                this.user.customer_name = value?.customer.name;
                this.user.designer_id = value?.id;
                this.user.designer_name = value?.fullName;
                this.user.first_name = value?.firstName;
                this.user.last_name = value?.lastName;
            },
        },
        selectedCustomer: {
            get() {
                return {
                    id: this.user.customer_id,
                    name: this.user.customer_name,
                };
            },
            set(value) {
                this.user.customer_id = value?.id;
                this.user.customer_name = value?.name;
            },
        },
        userNameLower: {
            get() {
                return this.user.username;
            },
            set(value) {
                this.user.username = value.toLowerCase();
            },
        },
    },
    watch: {
        reset() {
            this.resetForm();
        },
        userAdded(added) {
            if (added) {
                this.$emit("close");
                this.resetForm();
            }
        },
        availableUsername(value) {
            if (value) {
                this.user.username = value;
            }
        },
        "user.role": function update() {
            this.updateRoleOptions();
        },
        "user.first_name": function update() {
            this.getAvailableUsername();
        },
        "user.last_name": function update() {
            this.getAvailableUsername();
        },
    },
    created() {
        this.updateRoleOptions(false);
        if (this.userDetails) {
            this.user.first_name = this.userDetails?.first_name;
            this.user.last_name = this.userDetails?.last_name;
            this.user.email = this.userDetails?.email;
        }
    },
    methods: {
        createUser() {
            this.$emit("createUser", this.user);
        },
        getAvailableUsername() {
            if (this.user.first_name && this.user.last_name) {
                this.$emit("getAvailableUsername", {
                    fname: this.user.first_name,
                    lname: this.user.last_name,
                });
            }
        },
        resetForm() {
            // reset form values
            Object.keys(this.user).forEach((key) => {
                this.user[key] = null;
            });
            this.$nextTick(() => {
                // Reset validation
                this.$refs.form?.reset();
            });
        },
        updateRoleOptions(update = true) {
            switch (this.user.role) {
                case "ClientBasic":
                    this.showSelectCustomer = false;
                    this.showSelectDesigner = true;
                    break;
                case "ClientAdmin":
                    this.showSelectCustomer = true;
                    this.showSelectDesigner = false;
                    break;
                default:
                    this.showSelectCustomer = false;
                    this.showSelectDesigner = false;
            }
            if (update) {
                // clear out customer id and name when a new role is selected
                this.user.customer_id = null;
                this.user.customer_name = null;
                this.user.designer_id = null;
                this.user.designer_name = null;
            }
        },
        searchDesigners(search, loading) {
            this.searchDesignersText = search;
            this.$emit("searchDesigners", { search, loading });
        },
        searchCustomers(search, loading) {
            this.searchCustomersText = search;
            this.$emit("searchCustomers", { search, loading });
        },
    },
};
</script>

<style module>
.or {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
}
</style>
